import { endOfYear, startOfYear } from 'date-fns';
import { atom, getDefaultStore, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage, RESET } from 'jotai/utils';
import { useCallback } from 'react';
import { DateRange } from '~/components/inputs/DatePicker/CalendarRangePopover';
import { COMPANY_SWITCHED_EVENT } from '~/hooks/useActiveCompany';
import { BookkeepingAdjustmentGroup, BookkeepingTransactionFilters } from '~/types/general-bookkeeping';

const exportingJournalPDFAtom = atom<string | null>(null);

export const useIsExportingJournalPDF = (): boolean => {
  const [tracingId] = useAtom(exportingJournalPDFAtom);
  return tracingId !== null;
};

export const setExportingJournalPDF = (tracingId: string): void => {
  const store = getDefaultStore();
  store.set(exportingJournalPDFAtom, tracingId);

  // Safety timeout: clear loading state after 90s if WS event never arrives
  setTimeout(() => {
    if (store.get(exportingJournalPDFAtom) === tracingId) {
      store.set(exportingJournalPDFAtom, null);
    }
  }, 90_000);
};

export const clearExportingJournalPDF = (tracingId: string): boolean => {
  const store = getDefaultStore();
  if (store.get(exportingJournalPDFAtom) === tracingId) {
    store.set(exportingJournalPDFAtom, null);
    return true;
  }
  return false;
};

const fromAtom = atomWithStorage<string | null>(
  'bookkeeping-cash-book-from',
  startOfYear(new Date()).toISOString(),
  undefined,
  { getOnInit: true },
);

const toAtom = atomWithStorage<string | null>(
  'bookkeeping-cash-book-to',
  endOfYear(new Date()).toISOString(),
  undefined,
  { getOnInit: true },
);

const dateRangeAtom = atom<DateRange | null, [DateRange | null], void>(
  (get) => {
    const from = get(fromAtom);
    const to = get(toAtom);
    if (from && to) {
      return { start: new Date(from), end: new Date(to) };
    }
    return null;
  },
  (_get, set, value) => {
    if (!value) {
      set(fromAtom, null);
      set(toAtom, null);
      return;
    }

    set(fromAtom, value.start.toISOString());
    set(toAtom, value.end.toISOString());
  },
);

export const useDateFilter = () => useAtom(dateRangeAtom);

const defaultDateFilter = (): DateRange => ({
  start: startOfYear(new Date()),
  end: endOfYear(new Date()),
});

const isDefaultDateFilter = (dateRange: DateRange | null): boolean => {
  if (!dateRange) {
    return false;
  }

  const defaultDateRange = defaultDateFilter();

  return (
    dateRange.start.toISOString() === defaultDateRange.start.toISOString() &&
    dateRange.end.toISOString() === defaultDateRange.end.toISOString()
  );
};

const searchAtom = atomWithStorage<string>('bookkeeping-cash-book-search', '', undefined, {
  getOnInit: true,
});
export const useSearchFilter = () => useAtom(searchAtom);

const cashBookAmountRangeAtom = atomWithStorage<[number | null, number | null]>(
  'bookkeeping-cash-book-amount-range',
  [null, null],
  undefined,
  { getOnInit: true },
);
export const useCashBookAmountRangeFilter = () => useAtom(cashBookAmountRangeAtom);

const adjustmentGroupsAtom = atomWithStorage<BookkeepingAdjustmentGroup[]>(
  'bookkeeping-cash-book-adjustment-groups',
  [],
  undefined,
  { getOnInit: true },
);
export const useAdjustmentGroupsFilter = () => useAtom(adjustmentGroupsAtom);

export const useHasActiveCashBookFilters = (): boolean => {
  const dateRange = useAtomValue(dateRangeAtom);
  const search = useAtomValue(searchAtom);
  const cashBookAmountRange = useAtomValue(cashBookAmountRangeAtom);
  const adjustmentGroups = useAtomValue(adjustmentGroupsAtom);

  const hasNonDefaultDate = !isDefaultDateFilter(dateRange);
  const hasSearch = search !== '';
  const hasAmountRange = cashBookAmountRange.some((value) => value !== null);
  const hasAdjustmentGroups = adjustmentGroups.length > 0;

  return hasNonDefaultDate || hasSearch || hasAmountRange || hasAdjustmentGroups;
};

export const useResetCashBookFilters = () => {
  const setFrom = useSetAtom(fromAtom);
  const setTo = useSetAtom(toAtom);
  const setSearch = useSetAtom(searchAtom);
  const setCashBookAmountRange = useSetAtom(cashBookAmountRangeAtom);
  const setAdjustmentGroups = useSetAtom(adjustmentGroupsAtom);

  return useCallback(() => {
    setFrom(RESET);
    setTo(RESET);
    setSearch(RESET);
    setCashBookAmountRange(RESET);
    setAdjustmentGroups(RESET);
  }, [setCashBookAmountRange, setAdjustmentGroups, setFrom, setSearch, setTo]);
};

interface ScrollPositionState {
  scrollTop: number;
  filters: BookkeepingTransactionFilters;
}

const scrollPositionAtom = atom<ScrollPositionState | null>(null);

export const useSetScrollPosition = () => useSetAtom(scrollPositionAtom);
export const getScrollPosition = () => getDefaultStore().get(scrollPositionAtom);

// Reset all filters and export state when the company changes
window.addEventListener(COMPANY_SWITCHED_EVENT, () => {
  const store = getDefaultStore();
  store.set(fromAtom, RESET);
  store.set(toAtom, RESET);
  store.set(searchAtom, RESET);
  store.set(cashBookAmountRangeAtom, RESET);
  store.set(adjustmentGroupsAtom, RESET);
  store.set(exportingJournalPDFAtom, null);
});
